/**
 * A "Back" control on the signed-out auth screens.
 *
 * The auth screens live in `core/` (login-screen.ts and friends), which a game repo
 * must not edit — template merges would conflict on every future pull. So the
 * button is attached from GAME code after core has rendered, rather than by
 * changing the shared screens.
 *
 * Why it is needed at all: the player arrives at /login from the marketing site's
 * Play button, and once there the only ways out were the browser's back arrow or
 * editing the URL. A signed-out visitor who wants to read the guides or the FAQ
 * first had no route back to them.
 *
 * It sits OUTSIDE the wooden signboard, top-left of the page, so it never collides
 * with the board's painted slots — those are positioned as percentages of the board
 * and adding anything inside it would push the fields off their parchment.
 */

const BACK_ID = 'tb-auth-back';

/** Where "back" goes: the marketing landing, which the SPA now renders at "/". */
const SITE_URL = '/';

/**
 * Mount the back button over the current auth screen.
 *
 * Returns a teardown that the router composes with core's own teardown, so
 * navigating away removes it — a leftover fixed-position button would sit on top
 * of the game.
 */
export function attachAuthBack(): () => void {
  document.getElementById(BACK_ID)?.remove();

  const back = document.createElement('a');
  back.id = BACK_ID;
  back.href = SITE_URL;
  back.textContent = '← Back';
  back.setAttribute('aria-label', 'Back to the Terraboma site');
  // Fixed rather than absolute: the auth wrapper is a centred grid whose height
  // tracks the board, so an absolutely-positioned child would ride up and down
  // with the board's size on short viewports.
  //
  // min-height 40px because this is the one control on the page a phone user is
  // most likely to reach for by accident-of-thumb, and the auth screens already
  // hold every other target to that floor (see the pointer:coarse block in app.css).
  back.style.cssText = `position:fixed;left:14px;top:14px;z-index:40;
    display:inline-flex;align-items:center;min-height:40px;padding:8px 14px;
    background:#3a2e24;color:#e9d6bd;border:3px solid #6b4f34;border-radius:8px;
    font-family:"Terraboma","CuteFantasy",var(--font-system,sans-serif);font-size:14px;
    text-decoration:none;line-height:1;
    box-shadow:inset 0 -3px 0 rgba(0,0,0,.35), inset 0 2px 0 rgba(255,215,120,.14)`;
  // A hard navigation, not the href alone: the SPA installs a global click handler
  // that swallows same-origin anchor clicks and routes them internally, and from an
  // auth screen that handler re-rendered /login instead of leaving it. Assigning
  // location forces a real load, so the router re-enters cleanly on the landing.
  back.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.assign(SITE_URL);
  });
  back.addEventListener('pointerenter', () => { back.style.borderColor = '#e8c15a'; back.style.color = '#ffd97a'; });
  back.addEventListener('pointerleave', () => { back.style.borderColor = '#6b4f34'; back.style.color = '#e9d6bd'; });

  document.body.appendChild(back);
  return () => back.remove();
}
